import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import classNames from 'classnames';
import { Icon } from '@material-ui/core';

import { maxItemPerPage, showItemsMaxCountAroundCurrentPage, sortConditions } from './constants';
import { createCustomerListApiParameter } from './functions';
import { SCREEN, TRANSITION_SOURCE_PAGE_STATE } from '../../../Common';
import Button from '../../atoms/Button';
import CircularProgress from '../../atoms/CircularProgress';
import Select from '../../atoms/Select';
import PaginationWithoutCache from '../../molecules/PaginationWithoutCache';
import FilterSettingDrawer from '../../organisms/FilterSettingDrawer';
import ContentHeader from '../../organisms/ContentHeader';
import CustomerListTable from '../../organisms/CustomerListTable';
import SearchConditionInput from '../../../container/molecules/SearchConditionInputContainer';
import { CustomerInfoForList } from '../../../external/CustomerListApi';

import { actions } from '../../../redux/modules/Actions';
import { AppState } from '../../../redux/modules/store';
import { updateRefresh } from '../../../redux/operations/CustomerList';
import { SearchConditionState } from '../../../redux/reducers/SearchConditionReducer';
import { SearchCustomerListConditionState } from '../../../redux/reducers/SearchCustomerListConditionReducer';
import { StoreSelectorState } from '../../../redux/reducers/StoreSelectorReducer';
import { UserState } from '../../../redux/reducers/UserReducer';

export interface CustomerListSearchScreenProps {
  // ユーザーID（クルーコード）
  userId: string;
  // クルーモジュールかどうかのフラグ
  isCrewModule: boolean;
  // 検索条件（絞り込み条件、並び順、ページング情報などを含む）
  searchCondition: SearchCustomerListConditionState;
  // 検索条件（対象店舗のリスト）
  targetStoreList: string[];
}

/**
 * コンポーネント名
 */
export const componentName = 'CustomerListSearchScreen';

const CustomerListSearchScreen: React.FC<CustomerListSearchScreenProps> = ({
  userId,
  isCrewModule,
  searchCondition,
  targetStoreList,
  children,
}) => {
  const history = useHistory();
  const reduxDispatch = useDispatch();

  /**
   * 顧客一覧取得APIのローディングステータス
   */
  const fetchLoadingStatus = useSelector((state: AppState) => state.loadingStatus.fetchCustomerInfoList);

  // local state
  /**
   * ドロワーの表示状態
   */
  const [isOpenDrawer, setIsOpenDrawer] = useState(false);
  const [searchBranchCount, setSearchBranchCount] = useState<number>(0);
  const [localFilterState, setLocalFilterState] = useState<SearchConditionState>(searchCondition.filterState);
  const [localSortKey, setLocalSortKey] = useState(searchCondition.sortKey);
  const [localCurrentPage, setLocalCurrentPage] = useState(searchCondition.currentPage);
  const [localHit, setLocalHit] = useState<number>(0);
  const [localCustomerResult, setLocalCustomerResult] = useState<CustomerInfoForList[]>([]);

  // refs
  // 初回データ取得を1回だけに制御するためのフラグ
  const didRunInitialFetchRef = useRef<boolean>(false);
  // Drawerマウント直後の自動searchコールバックを1回だけ無視するためのフラグ
  const isFirstDrawerAutoSearchRef = useRef<boolean>(true);
  // employee/agencyで店舗変更を検知するため、前回のtargetStoreListを保持
  const prevTargetStoreListRef = useRef<string>(targetStoreList.join(','));

  /**
   * 画面内ボタンクリックにて画面遷移する場合は、遷移先から戻る際に、検索条件を保持するため、ローカルステートに保存した検索条件をグローバルステートに保存する関数
   */
  const updateGlobalStateByLocalState = () => {
    reduxDispatch(actions.updateCustomerListSearchCondition.changeFilterState(localFilterState));
    reduxDispatch(actions.updateCustomerListSearchCondition.changeCurrentPage(localCurrentPage));
    reduxDispatch(actions.updateCustomerListSearchCondition.changeSortKey(localSortKey));
  };

  const executeSearch = async (filterState: SearchConditionState, targetStores: string[], currentPage: number, sortKey: string) => {
    const request = createCustomerListApiParameter(userId, isCrewModule, filterState, targetStores, currentPage, sortKey);
    const response = await updateRefresh(request, reduxDispatch);
    setLocalHit(response.hit);
    setLocalCustomerResult(response.customerResult);
  };

  const handleClickRegistCustomer = () => {
    updateGlobalStateByLocalState();
    history.push(`${SCREEN.CUSTOMER_CREATE.URL}`, TRANSITION_SOURCE_PAGE_STATE.CUSTOMER_INFO_LIST);
  };

  const handleRefreshPage = () => {
    void executeSearch(localFilterState, targetStoreList, localCurrentPage, localSortKey);
  };

  // 画面初回表示時のみ顧客一覧を取得する（StrictMode再描画や state 更新での再実行を防ぐ）
  useEffect(() => {
    if (didRunInitialFetchRef.current) {
      return;
    }
    didRunInitialFetchRef.current = true;
    void executeSearch(localFilterState, targetStoreList, localCurrentPage, localSortKey);
  }, [userId, isCrewModule, localFilterState, targetStoreList, localCurrentPage, localSortKey, reduxDispatch]);

  // employee/agency では店舗バッジ変更に追従して検索する（crew は絞り込みボタン起点のみ）
  useEffect(() => {
    if (isCrewModule) {
      return;
    }

    const currentTargetStoreList = targetStoreList.join(',');
    if (prevTargetStoreListRef.current === currentTargetStoreList) {
      return;
    }

    prevTargetStoreListRef.current = currentTargetStoreList;
    void executeSearch(localFilterState, targetStoreList, localCurrentPage, localSortKey);
  }, [isCrewModule, targetStoreList, localFilterState, localCurrentPage, localSortKey]);

  const handleOpenFilterDrawer = () => {
    setIsOpenDrawer(true);
  };

  // 並び順変更は即時反映が仕様のため、state更新後にその場で再検索する
  const handleChangeSortDropdown = (selected: string) => {
    if (localSortKey === selected) {
      return;
    }

    const nextPage = 1;

    setLocalSortKey(selected);
    setLocalCurrentPage(nextPage);

    void executeSearch(localFilterState, targetStoreList, nextPage, selected);
  };

  // ページング操作も即時反映が仕様のため、選択ページ確定時に再検索する
  const handleChangeCurrentPage = (selectedPageNumber: number) => {
    setLocalCurrentPage(selectedPageNumber);
    void executeSearch(localFilterState, targetStoreList, selectedPageNumber, localSortKey);
  };

  const handleCloseFilterDrawer = () => {
    setIsOpenDrawer(false);
  };

  // FilterSettingDrawerから渡されるsearchコールバック。
  // 「絞り込み」押下時の明示検索に加えて、モジュール仕様に応じた検索トリガーをここで吸収する。
  const handleSearchOnDrawer = (
    _loginUser: UserState,
    storeSelector: StoreSelectorState,
    filterConditions: SearchConditionState,
    isPagingInitialized: boolean,
    dropDownSelect?: string,
  ) => {
    // Drawer初期化時に発火する自動searchは1回だけスキップする（不要なAPI実行を防ぐ）
    const isDrawerSearchContext = isPagingInitialized || dropDownSelect != null;
    if (!isOpenDrawer && isFirstDrawerAutoSearchRef.current && isDrawerSearchContext) {
      isFirstDrawerAutoSearchRef.current = false;
      return;
    }

    // crewはprops由来の店舗一覧、employee/agencyはstoreSelector選択状態を検索条件に使う
    const nextTargetStoreList = isCrewModule ? targetStoreList : storeSelector.selectedStores;

    setLocalCurrentPage(1);
    setLocalFilterState(filterConditions);
    void executeSearch(filterConditions, nextTargetStoreList, 1, localSortKey);
  };

  return (
    <div className='layout-tab'>
      {/* header */}
      <div className={classNames('layout-tab__header', 'u-bg-white')}>
        <div className='container'>
          <ContentHeader
            screenId={SCREEN.CUSTOMER_INFO_LIST.ID}
            screenName={SCREEN.CUSTOMER_INFO_LIST.NAME}
            titleIcon={
              <div
                className={classNames(
                  'dashboard-summary-icon-wrap',
                  'dashboard-summary-icon-wrap--small',
                  'dashboard-summary-icon-wrap--search',
                  'u-float-left',
                  'u-mr8',
                )}>
                <Icon className='dashboard-summary-icon'>group</Icon>
              </div>
            }
            statusType=''
            loadingStatus={fetchLoadingStatus}
            data-testid={`${componentName}-content-header`}
            data-testid-e2e={`${componentName}-content-header`}
            customRefresh={handleRefreshPage}>
            {/* crewモジュールの場合にのみ表示します */}
            {isCrewModule && (
              <Button
                className='u-mr8'
                onClick={handleClickRegistCustomer}
                leftIcon='add'
                label='お客さまを登録'
                data-testid={`${componentName}-registerButton`}
                data-testid-e2e={`${componentName}-registerButton`}
              />
            )}
            <SearchConditionInput searchBranchCount={searchBranchCount} toggleDrower={handleOpenFilterDrawer} />
          </ContentHeader>
        </div>
      </div>
      <hr className='uc-divider' />
      <div className='layout-tab__content'>
        <div className={classNames('container', 'u-flex', 'u-flex-column', 'u-h-full')}>
          {fetchLoadingStatus.isInitial ? (
            <></>
          ) : fetchLoadingStatus.isLoading ? (
            <div className='layout-center'>
              <div
                className={'layout-center'}
                data-testid={`${componentName}-loading-area`}
                data-testid-e2e={`${componentName}-loading-area`}>
                <div className='u-text-center'>
                  <CircularProgress isIndeterminate hasBackground={false} />
                  <p>ページを読み込んでいます...</p>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div className='clearfix u-mb16'>
                <div
                  className='u-float-left'
                  data-testid={`${componentName}-sort-condition-area`}
                  data-testid-e2e={`${componentName}-sort-condition-area`}>
                  <Select
                    className='uc-card__header-right'
                    value={localSortKey}
                    onChange={(e) => handleChangeSortDropdown(e.target.value)}
                    data-testid={`${componentName}-sort-dropdown`}
                    data-testid-e2e={`${componentName}-sort-dropdown`}>
                    {sortConditions.map((item) => (
                      <Select.Option value={item.key} key={item.key}>
                        {item.label}
                      </Select.Option>
                    ))}
                  </Select>
                </div>
                <div
                  className='u-float-right'
                  data-testid={`${componentName}-paging-area`}
                  data-testid-e2e={`${componentName}-paging-area`}>
                  {/* paging information */}
                  <PaginationWithoutCache
                    maxItemPerPage={maxItemPerPage}
                    totalCount={localHit}
                    currentPageNumber={localCurrentPage}
                    itemCountPerPage={localCustomerResult.length}
                    showItemsMaxCountAroundCurrentPage={showItemsMaxCountAroundCurrentPage}
                    onSelectPageNumber={handleChangeCurrentPage}
                  />
                </div>
              </div>
              <CustomerListTable
                hit={localHit}
                customerResult={localCustomerResult}
                updateGlobalStateByLocalState={updateGlobalStateByLocalState}
              />
              <FilterSettingDrawer
                isOpen={isOpenDrawer}
                isSearchImprovement={isCrewModule}
                isMailAddressSelect={false}
                disableAutoSearchByStoreSelector={true}
                setSearchBranchCount={(count) => setSearchBranchCount(count)}
                onClose={handleCloseFilterDrawer}
                search={handleSearchOnDrawer}
                isCalledByCustomerList={true}>
                {children}
              </FilterSettingDrawer>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerListSearchScreen;
